import { Controller } from "stimulus"

// #1956: Nieuw trainingsschema (exercise_schedules/new). Zoekt, filtert en
// sorteert de templatekaarten in de pagina, zet het gekozen template links
// neer en rekent de planning uit:
//
//   trainingen per week × aantal weken = totaal aantal sessies
//
// Het schema doorloopt de trainingen van het template (A/B/C) op volgorde;
// gaat het totaal niet op in volle rondes, dan is de laatste ronde onvolledig
// (ronde 15, op verzoek van Sam). De server rekent hetzelfde na
// (Schemaplanning); dit is alleen voor de trainer om direct te zien wat hij
// aanmaakt.
export default class extends Controller {
  static targets = [
    "templateId", "naam", "zoek", "chip", "doel", "type", "sorteer", "lijst", "kaart", "leeg", "aantal",
    "filterpaneel", "filterAantal",
    "gekozenLeeg", "gekozenInhoud", "gekozenIcoon", "gekozenNaam", "gekozenMeta", "gekozenTrainingen",
    "weken", "perWeek", "perWeekWeergave", "perWeekHandmatig", "info", "tip", "aanpassen", "terugNaarTemplate",
    "bronTekst", "totaal", "berekening", "verdeling", "melding", "knop", "knopTekst"
  ]

  static values = { teksten: Object, maxWeken: Number, maxPerWeek: Number }

  connect() {
    this.perWeekFilter = ""
    this.handmatigGekozen = false
    this.bezig = false
    // Op een smal scherm staan de filters ingeklapt
    if (this.hasFilterpaneelTarget && window.matchMedia("(max-width: 699px)").matches) {
      this.filterpaneelTarget.open = false
    }
    const kaart = this.kaartVoor(this.templateIdTarget.value)
    if (kaart) {
      // Na een foutmelding: dezelfde keuze, en een zelf ingevulde frequentie blijft staan
      const ingevuld = this.perWeekTarget.value
      this.toonTemplate(kaart)
      if (ingevuld && ingevuld !== kaart.dataset.perWeek) {
        this.handmatigGekozen = true
        this.perWeekTarget.value = ingevuld
        this.toonFrequentie(kaart)
      }
    }
    this.bereken()
  }

  // ---------- zoeken, filteren en sorteren ----------

  filter() {
    const termen = this.hasZoekTarget ? this.zoekTarget.value.split(/\s+/).map(this.normaliseer).filter(Boolean) : []
    const doelen = this.doelTargets.filter((vink) => vink.checked).map((vink) => vink.value)
    const types = this.typeTargets.filter((vink) => vink.checked).map((vink) => vink.value)
    let zichtbaar = 0

    this.kaartTargets.forEach((kaart) => {
      const tekst = this.normaliseer(kaart.dataset.zoek)
      const kaartDoelen = (kaart.dataset.doelen || "").split("|").filter(Boolean)
      const past = termen.every((term) => tekst.includes(term)) &&
        (this.perWeekFilter === "" || kaart.dataset.perWeek === this.perWeekFilter) &&
        (doelen.length === 0 || kaartDoelen.some((doel) => doelen.includes(doel))) &&
        (types.length === 0 || types.includes(kaart.dataset.type))
      kaart.hidden = !past
      if (past) zichtbaar += 1
    })

    if (this.hasLeegTarget) this.leegTarget.hidden = zichtbaar > 0
    if (this.hasAantalTarget) {
      this.aantalTarget.textContent = this.meervoud(zichtbaar, this.tekstenValue.aantal_een, this.tekstenValue.aantal_meer)
    }
    if (this.hasFilterAantalTarget) {
      const aantal = doelen.length + types.length
      this.filterAantalTarget.textContent = String(aantal)
      this.filterAantalTarget.hidden = aantal === 0
    }
  }

  // "Upper / Lower" vindt "Upper/Lower", "coordinatie" vindt "Coördinatie"
  normaliseer(tekst) {
    return (tekst || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]/g, "")
  }

  kiesFrequentie(event) {
    const knop = event.currentTarget
    this.perWeekFilter = knop.dataset.perWeek || ""
    this.chipTargets.forEach((chip) => {
      const actief = chip === knop
      chip.classList.toggle("is-actief", actief)
      chip.setAttribute("aria-pressed", actief ? "true" : "false")
    })
    this.filter()
  }

  sorteer() {
    const manier = this.sorteerTarget.value
    const naam = (kaart) => (kaart.dataset.naam || "").toLocaleLowerCase()
    const datum = (kaart) => parseInt(kaart.dataset.aangemaakt || "0", 10)
    const vergelijk = {
      naam_az: (a, b) => naam(a).localeCompare(naam(b)),
      naam_za: (a, b) => naam(b).localeCompare(naam(a)),
      nieuw: (a, b) => datum(b) - datum(a),
      oud: (a, b) => datum(a) - datum(b)
    }[manier]
    if (!vergelijk) return

    this.kaartTargets.slice().sort(vergelijk).forEach((kaart) => this.lijstTarget.appendChild(kaart))
  }

  wisFilters() {
    if (this.hasZoekTarget) this.zoekTarget.value = ""
    this.doelTargets.concat(this.typeTargets).forEach((vink) => { vink.checked = false })
    const alle = this.chipTargets.find((chip) => (chip.dataset.perWeek || "") === "")
    if (alle) alle.click()
    else this.filter()
  }

  geenVerzending(event) {
    event.preventDefault()
  }

  // ---------- een template kiezen ----------

  kies(event) {
    this.handmatigGekozen = false
    this.toonTemplate(event.currentTarget)
    this.bereken()
  }

  kaartVoor(id) {
    if (!id) return null
    return this.kaartTargets.find((kaart) => kaart.dataset.id === String(id)) || null
  }

  toonTemplate(kaart) {
    this.templateIdTarget.value = kaart.dataset.id
    this.kaartTargets.forEach((k) => {
      const gekozen = k === kaart
      k.classList.toggle("is-gekozen", gekozen)
      k.setAttribute("aria-pressed", gekozen ? "true" : "false")
    })

    this.gekozenLeegTarget.hidden = true
    this.gekozenInhoudTarget.hidden = false
    this.gekozenNaamTarget.textContent = kaart.dataset.naam
    const icoon = kaart.querySelector("[data-icoon]")
    this.gekozenIcoonTarget.innerHTML = icoon ? icoon.innerHTML : ""
    const meta = kaart.querySelector(".ns-meta")
    this.gekozenMetaTarget.replaceChildren(...(meta ? Array.from(meta.children).map((deel) => deel.cloneNode(true)) : []))

    let namen = []
    try { namen = JSON.parse(kaart.dataset.namen || "[]") } catch (e) { namen = [] }
    const letters = (kaart.dataset.letters || "").split("/")
    this.gekozenTrainingenTarget.replaceChildren(...namen.map((naam, i) => {
      const regel = document.createElement("li")
      const letter = document.createElement("span")
      letter.className = "ns-letter"
      letter.textContent = letters[i] || String(i + 1)
      regel.append(letter, document.createTextNode(naam))
      return regel
    }))

    // Een lege schemanaam wordt de templatenaam: laat dat alvast zien
    if (this.hasNaamTarget) this.naamTarget.placeholder = kaart.dataset.naam || this.tekstenValue.naam_placeholder

    this.toonFrequentie(kaart)
  }

  // De frequentie uit het template, of zelf invullen als het template er
  // geen geeft (of de trainer hem wil aanpassen)
  toonFrequentie(kaart) {
    const afgeleid = kaart.dataset.perWeek
    const bron = kaart.dataset.bron
    const handmatig = this.handmatigGekozen || !afgeleid

    this.perWeekTarget.max = String(Math.max(this.maxPerWeekValue, parseInt(afgeleid || "0", 10)))
    if (!handmatig) this.perWeekTarget.value = afgeleid
    else if (!afgeleid && !this.handmatigGekozen) this.perWeekTarget.value = ""
    this.perWeekTarget.required = true

    this.perWeekWeergaveTarget.hidden = handmatig
    this.perWeekHandmatigTarget.hidden = !handmatig
    this.aanpassenTarget.hidden = !afgeleid || this.handmatigGekozen
    this.terugNaarTemplateTarget.hidden = !(afgeleid && this.handmatigGekozen)
    if (afgeleid) this.perWeekWeergaveTarget.textContent = this.perWeekTekst(parseInt(afgeleid, 10))

    const t = this.tekstenValue
    let uitleg = t.bron_handmatig
    if (bron === "template") uitleg = t.bron_template
    if (bron === "trainingen") uitleg = this.vul(t.bron_trainingen, { count: kaart.dataset.trainingen, letters: kaart.dataset.letters })
    this.bronTekstTarget.textContent = uitleg
    // Zonder frequentie in het template meteen uitleggen waarom het veld er staat
    if (!afgeleid) this.zetInfo(true)
  }

  wisselInfo() {
    this.zetInfo(this.tipTarget.hidden)
  }

  zetInfo(open) {
    this.tipTarget.hidden = !open
    this.infoTarget.setAttribute("aria-expanded", open ? "true" : "false")
  }

  handmatig() {
    this.handmatigGekozen = true
    const kaart = this.kaartVoor(this.templateIdTarget.value)
    if (kaart) this.toonFrequentie(kaart)
    this.perWeekTarget.focus()
    this.perWeekTarget.select()
    this.bereken()
  }

  uitTemplate() {
    this.handmatigGekozen = false
    const kaart = this.kaartVoor(this.templateIdTarget.value)
    if (kaart) this.toonFrequentie(kaart)
    this.bereken()
  }

  naarZoeker() {
    const zoeker = document.getElementById("templatezoeker")
    if (zoeker) zoeker.scrollIntoView({ behavior: "smooth", block: "start" })
    if (this.hasZoekTarget) this.zoekTarget.focus({ preventScroll: true })
  }

  // ---------- planning ----------

  minderWeken() {
    this.zetWeken((this.getal(this.wekenTarget.value) || 1) - 1)
  }

  meerWeken() {
    this.zetWeken((this.getal(this.wekenTarget.value) || 0) + 1)
  }

  zetWeken(weken) {
    this.wekenTarget.value = String(Math.min(Math.max(weken, 1), this.maxWekenValue))
    this.bereken()
  }

  bereken() {
    const t = this.tekstenValue
    const kaart = this.kaartVoor(this.templateIdTarget.value)
    const weken = this.getal(this.wekenTarget.value)
    const perWeek = this.getal(this.perWeekTarget.value)
    const trainingen = kaart ? parseInt(kaart.dataset.trainingen || "0", 10) : 0
    const maxPerWeek = parseInt(this.perWeekTarget.max || String(this.maxPerWeekValue), 10)

    let fout = null
    if (!kaart) fout = t.kies_eerst
    else if (trainingen < 1) fout = t.geen_trainingen
    else if (!weken || weken > this.maxWekenValue) fout = t.weken_ongeldig
    else if (!perWeek || perWeek > maxPerWeek) fout = t.per_week_ongeldig

    // Het totaal staat er alleen als het ook echt zo wordt aangemaakt, zodat
    // de trainer nooit twee verschillende aantallen ziet.
    if (!fout) {
      const totaal = weken * perWeek
      this.totaalTarget.textContent = this.meervoud(totaal, t.sessies_een, t.sessies_meer)
      this.berekeningTarget.textContent = this.vul(t.berekening, {
        per_week: perWeek,
        weken: this.meervoud(weken, t.weken_een, t.weken_meer),
        totaal: this.meervoud(totaal, t.sessies_een, t.sessies_meer)
      })
      this.verdelingTarget.textContent = this.verdeling(kaart.dataset.letters || "", trainingen, totaal)
    } else {
      this.totaalTarget.textContent = "–"
      this.berekeningTarget.textContent = ""
      this.verdelingTarget.textContent = ""
    }

    const melden = fout && fout !== t.kies_eerst
    this.meldingTarget.hidden = !melden
    this.meldingTarget.textContent = melden ? fout : ""
    this.geldig = !fout
    this.knopTarget.setAttribute("aria-disabled", this.geldig ? "false" : "true")
    this.knopTarget.classList.toggle("is-uit", !this.geldig)
  }

  // A/B, 15 sessies: "A/B komt 7× volledig terug; de laatste ronde is alleen A."
  verdeling(letters, trainingen, totaal) {
    const t = this.tekstenValue
    const volle = Math.floor(totaal / trainingen)
    const rest = totaal % trainingen
    if (rest === 0) return this.vul(t.verdeling, { letters, herhalingen: volle })

    const restLetters = letters.split("/").slice(0, rest).join("/")
    if (volle === 0) return this.vul(t.verdeling_alleen, { letters, rest: restLetters })
    return this.vul(t.verdeling_rest, { letters, volle, rest: restLetters })
  }

  // ---------- aanmaken ----------

  verstuur(event) {
    this.bereken()
    if (this.bezig || !this.geldig) {
      event.preventDefault()
      if (!this.geldig) {
        const doel = this.templateIdTarget.value ? this.meldingTarget : document.getElementById("templatezoeker")
        if (doel) doel.scrollIntoView({ behavior: "smooth", block: "center" })
        if (!this.templateIdTarget.value) {
          this.meldingTarget.hidden = false
          this.meldingTarget.textContent = this.tekstenValue.kies_eerst
        }
      }
      return
    }
    // Eén keer versturen: een tweede klik maakt geen tweede schema
    this.bezig = true
    this.knopTarget.setAttribute("aria-disabled", "true")
    this.knopTarget.classList.add("is-bezig")
    this.knopTekstTarget.textContent = this.tekstenValue.bezig
  }

  // ---------- hulpjes ----------

  getal(waarde) {
    const tekst = String(waarde || "").trim()
    return /^\d+$/.test(tekst) ? parseInt(tekst, 10) : null
  }

  perWeekTekst(aantal) {
    return this.meervoud(aantal, this.tekstenValue.per_week_een, this.tekstenValue.per_week_meer)
  }

  meervoud(aantal, een, meer) {
    return (aantal === 1 ? een : meer).replace(/\b(1|99)\b/, String(aantal))
  }

  vul(tekst, waarden) {
    return Object.entries(waarden).reduce((uit, [sleutel, waarde]) => uit.split(`%{${sleutel}}`).join(String(waarde)), tekst)
  }
}
